//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';
import { ProgressBar } from 'unison';

import { ConnectPanelActions } from '@catalog-ingestion/actions/connectPanelActions';
import { GoogleCatalogIngestionModalActions } from '@catalog-ingestion/actions/googleCatalogIngestionModalActions';
import { ValidationErrorLayout } from '@catalog-ingestion/components/validation-panel/ValidationErrorLayout';
import productTaggingImage from '@images/product_tag-animated_image.gif';
import { ICuralateStore } from '@models/AppState';

import { ReduxDispatch } from 'app/curalate.store';

function mapStateToProps(state: ICuralateStore) {
  return {
    currentPanel: state.catalogIngestionReducers.googleCatalogIngestionModal.currentPanel,
    isCatalogOnboarded: state.catalogIngestionReducers.googleCatalogIngestionModal.isCatalogOnboarded,
  };
}

const mapDispatchToProps = (dispatch: ReduxDispatch) => {
  return {
    actions: {
      modal: bindActionCreators(GoogleCatalogIngestionModalActions, dispatch),
      connectPanel: bindActionCreators(ConnectPanelActions, dispatch),
    },
  };
};

type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

export class ProcessPanel extends React.Component<Props> {

  public componentDidMount() {
    this.props.actions.modal.onboardCatalog();
  }

  public onReset = () => {
    const actions = this.props.actions;
    actions.connectPanel.toggleIsValidating();
    actions.modal.resetGoogleCatalogIngestionState();
    actions.modal.openGoogleCatalogIngestionModal();
  }

  public render() {
    return (
      <Sheet>
        {
          // the null state here is the initial state of isCatalogOnboarded, so null is the loading state until
          // the endpoint we hit to update that state returns and sets isCatalogOnboarded to either true or false.
          // Then it will show either the error state or the instructions on how to tag if the onboarding was
          // successful.

          this.props.isCatalogOnboarded === null ?
          <ContentContainer>
            <ProgressBar
              value={ 0.0 }
              isIndeterminate={ true }
              className='cu-progress-bar-success cu-drag-and-drop-progress-bar'
            />
          </ContentContainer>
          :
          !this.props.isCatalogOnboarded ?
          <ValidationErrorLayout
            errorMessage='Nuts! You have already used this url to upload a catalog.'
            errorDescription='You may want to restart this process with a different url'
            buttonText='Restart'
            onButtonClick={ this.onReset }
          />
          :
          <>
            <Instructions>
              <div className='cu-text-subtitle'>
                Your catalog will be ready soon!
              </div>
              <InstructionBody>
                It can take up to an hour for products to begin showing in your account. In the mean time,
                check out this article to learn more about product tagging.
                <div className='cu-text-body-2'>We'll send you an email when your catalog is ready to view.</div>
              </InstructionBody>
              <a
                className='cu-text-link-primary'
                target='_blank'
                href='http://developers.curalate.com/guide/product-catalogs/'
              >
                  Learn More About Product Tagging
              </a>
            </Instructions>
            <ContentContainer>
              <ProductTaggingImage src={ productTaggingImage }/>
            </ContentContainer>
          </>
        }
      </Sheet>
    );
  }
}

const Sheet = styled.div.attrs({ className: 'cu-sheet-low' })`
  padding: ${({theme}) => theme.spacings[4]};
`;

const InstructionBody = styled.div.attrs({ className: 'cu-text-body' })`
  padding-top: ${({theme}) => theme.spacings[2]};
  padding-bottom: ${({theme}) => theme.spacings[4]};
`;

const Instructions = styled.div`
  width: 100%;
  padding-top: ${({theme}) => theme.spacings['2']};
`;

const ContentContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: ${({theme}) => theme.spacings['3']};
`;

const ProductTaggingImage = styled.img`
  width: 70%;
`;

export default connect(mapStateToProps, mapDispatchToProps)(ProcessPanel);
